import { useState, useRef, useEffect } from 'react';

export interface UseDraggableResizableReturn {
  // Refs
  dialogPaperRef: React.RefObject<HTMLDivElement>;
  /** Set to true when drag or resize ends (mouseup). Parent should skip closing on backdrop click when this is true, then clear it. */
  interactionJustEndedRef: React.MutableRefObject<boolean>;
  
  // State
  position: { x: number; y: number };
  size: { width: number | null; height: number | null };
  isDragging: boolean;
  isResizing: boolean;
  
  // Handlers
  handleMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleResizeStart: (e: React.MouseEvent<HTMLDivElement>) => void;
  
  // Props for Dialog / Paper
  paperProps: {
    ref: React.RefObject<HTMLDivElement>;
    sx: {
      transform?: string;
      transition: string;
      height?: string;
      width?: string;
      minWidth: string;
      minHeight: string;
      position: 'relative';
    };
  };
}

type ResizeState = {
  x: number;
  y: number;
  width: number;
  height: number;
  anchorLeft: number;
  anchorTop: number;
  translateX: number;
  translateY: number;
};

const MIN_WIDTH = 400;
const MIN_HEIGHT = 300;

export interface UseDraggableResizableOptions {
  /** When true, capture and lock the dialog size when it opens so it doesn't shrink on content change (e.g. agenda change). */
  lockSizeOnOpen?: boolean;
  minWidth?: number;
  minHeight?: number;
  /** Limit resize handle to one axis — compact pickers use width-only so height stays shrink-wrapped. */
  resizeAxis?: "both" | "width" | "height";
}

export function useDraggableResizable(isOpen: boolean, options?: UseDraggableResizableOptions): UseDraggableResizableReturn {
  const {
    lockSizeOnOpen = false,
    minWidth = MIN_WIDTH,
    minHeight = MIN_HEIGHT,
    resizeAxis = "both",
  } = options ?? {};
  const allowWidthResize = resizeAxis === "both" || resizeAxis === "width";
  const allowHeightResize = resizeAxis === "both" || resizeAxis === "height";
  // Draggable state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const draggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const dialogPaperRef = useRef<HTMLDivElement | null>(null);
  
  // Resizable state
  const [size, setSize] = useState<{ width: number | null; height: number | null }>({ width: null, height: null });
  const [isResizing, setIsResizing] = useState(false);
  const resizingRef = useRef(false);
  const resizeStartRef = useRef<ResizeState>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    anchorLeft: 0,
    anchorTop: 0,
    translateX: 0,
    translateY: 0,
  });
  const resizeCurrentRef = useRef<{ width: number; height: number | null }>({ width: 0, height: null });
  const positionRef = useRef({ x: 0, y: 0 });
  const interactionJustEndedRef = useRef(false);

  // Draggable handlers
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Don't start drag if clicking on buttons or interactive elements
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('[role="button"]')) {
      return;
    }
    
    draggingRef.current = true;
    setIsDragging(true);
    positionRef.current = { x: position.x, y: position.y };
    dragStartRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!draggingRef.current || !dialogPaperRef.current) return;
      
      const modalRect = dialogPaperRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Calculate new position
      let newX = e.clientX - dragStartRef.current.x;
      let newY = e.clientY - dragStartRef.current.y;
      
      // Get the modal's dimensions
      const modalWidth = modalRect.width;
      const modalHeight = modalRect.height;
      
      // Minimum visible portion (in pixels) that must remain on screen
      const minVisiblePortion = 50;
      
      // Constrain top: prevent modal from going above the viewport (y >= 0)
      const maxNegativeOffsetY = Math.max(0, (viewportHeight - modalHeight) / 2);
      newY = Math.max(-maxNegativeOffsetY, newY);
      
      // Constrain bottom: allow going below but keep at least minVisiblePortion visible
      const maxPositiveOffsetY = (viewportHeight - modalHeight) / 2 + (modalHeight - minVisiblePortion);
      newY = Math.min(maxPositiveOffsetY, newY);
      
      // Constrain left: allow going left but keep at least minVisiblePortion visible
      const maxNegativeOffsetX = -(viewportWidth - modalWidth) / 2 - (modalWidth - minVisiblePortion);
      newX = Math.max(maxNegativeOffsetX, newX);
      
      // Constrain right: allow going right but keep at least minVisiblePortion visible
      const maxPositiveOffsetX = (viewportWidth - modalWidth) / 2 + (modalWidth - minVisiblePortion);
      newX = Math.min(maxPositiveOffsetX, newX);
      
      positionRef.current = { x: newX, y: newY };
      dialogPaperRef.current.style.transform = `translate(${newX}px, ${newY}px)`;
    };

    const handleMouseUp = () => {
      setPosition(positionRef.current);
      draggingRef.current = false;
      setIsDragging(false);
      interactionJustEndedRef.current = true;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
  };

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!dialogPaperRef.current) return;
    
    const rect = dialogPaperRef.current.getBoundingClientRect();
    resizingRef.current = true;
    setIsResizing(true);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: rect.width,
      height: rect.height,
      anchorLeft: rect.left,
      anchorTop: rect.top,
      translateX: positionRef.current.x,
      translateY: positionRef.current.y,
    };
    resizeCurrentRef.current = { width: rect.width, height: rect.height };
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizingRef.current || !dialogPaperRef.current) return;
      
      const deltaX = e.clientX - resizeStartRef.current.x;
      const deltaY = e.clientY - resizeStartRef.current.y;
      const viewportMaxWidth = Math.max(
        minWidth,
        window.innerWidth - resizeStartRef.current.anchorLeft - 16,
      );
      const viewportMaxHeight = Math.max(
        minHeight,
        window.innerHeight - resizeStartRef.current.anchorTop - 16,
      );

      const newWidth = allowWidthResize
        ? Math.min(
            viewportMaxWidth,
            Math.max(minWidth, resizeStartRef.current.width + deltaX),
          )
        : resizeStartRef.current.width;
      const newHeight = allowHeightResize
        ? Math.min(
            viewportMaxHeight,
            Math.max(minHeight, resizeStartRef.current.height + deltaY),
          )
        : resizeStartRef.current.height;

      resizeCurrentRef.current = { width: newWidth, height: allowHeightResize ? newHeight : null };
      if (allowWidthResize) {
        dialogPaperRef.current.style.width = `${newWidth}px`;
      }
      if (allowHeightResize) {
        dialogPaperRef.current.style.height = `${newHeight}px`;
      }

      // Keep top-left fixed while expanding from bottom-right (works after drag too).
      const newRect = dialogPaperRef.current.getBoundingClientRect();
      const driftX = newRect.left - resizeStartRef.current.anchorLeft;
      const driftY = newRect.top - resizeStartRef.current.anchorTop;
      const newTranslateX = resizeStartRef.current.translateX - driftX;
      const newTranslateY = resizeStartRef.current.translateY - driftY;
      positionRef.current = { x: newTranslateX, y: newTranslateY };
      dialogPaperRef.current.style.transform = `translate(${newTranslateX}px, ${newTranslateY}px)`;
    };

    const handleMouseUp = () => {
      setSize({
        width: allowWidthResize ? resizeCurrentRef.current.width : null,
        height: allowHeightResize ? resizeCurrentRef.current.height : null,
      });
      setPosition(positionRef.current);
      resizingRef.current = false;
      setIsResizing(false);
      interactionJustEndedRef.current = true;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'nwse-resize';
  };

  // Apply size from state to DOM when not resizing (e.g. after mouseup or initial)
  useEffect(() => {
    if (!dialogPaperRef.current || isResizing) return;
    if (size.width != null) dialogPaperRef.current.style.width = `${size.width}px`;
    if (allowHeightResize && size.height != null) {
      dialogPaperRef.current.style.height = `${size.height}px`;
    } else if (!allowHeightResize) {
      dialogPaperRef.current.style.height = "";
    }
  }, [allowHeightResize, size.width, size.height, isResizing]);

  // Reset position and size when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPosition({ x: 0, y: 0 });
      setSize({ width: null, height: null });
      if (dialogPaperRef.current) {
        dialogPaperRef.current.style.width = "";
        dialogPaperRef.current.style.height = "";
        dialogPaperRef.current.style.transform = "";
      }
    }
  }, [isOpen]);

  // When modal opens with lockSizeOnOpen, capture current dimensions after paint so the dialog keeps width/height (e.g. doesn't shrink on agenda change)
  useEffect(() => {
    if (!isOpen || !lockSizeOnOpen) return;
    const timer = setTimeout(() => {
      if (dialogPaperRef.current) {
        const rect = dialogPaperRef.current.getBoundingClientRect();
        if (rect.width >= minWidth && rect.height >= minHeight) {
          setSize({ width: rect.width, height: rect.height });
        }
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [isOpen, lockSizeOnOpen, minHeight, minWidth]);

  const paperProps = {
    ref: dialogPaperRef,
    sx: {
      height: allowHeightResize && size.height != null ? `${size.height}px` : undefined,
      width: size.width != null ? `${size.width}px` : undefined,
      minWidth: `${minWidth}px`,
      minHeight: allowHeightResize ? `${minHeight}px` : undefined,
      transform: position.x !== 0 || position.y !== 0 ? `translate(${position.x}px, ${position.y}px)` : 'none',
      transition: isDragging || isResizing ? 'none' : 'transform 0.1s ease-out',
      position: 'relative' as const,
    }
  };

  return {
    dialogPaperRef: dialogPaperRef as React.RefObject<HTMLDivElement>,
    interactionJustEndedRef,
    position,
    size,
    isDragging,
    isResizing,
    handleMouseDown,
    handleResizeStart,
    paperProps: paperProps as unknown as {
      ref: React.RefObject<HTMLDivElement>;
      sx: {
        transform?: string;
        transition: string;
        height?: string;
        width?: string;
        minWidth: string;
        minHeight: string;
        position: 'relative';
      };
    },
  };
}
